/**
 * FIRESTORE SERVICE v3.0 - ULTRA OPTIMIZED
 * ✅ Eliminada duplicación de código
 * ✅ Funciones genéricas reutilizables
 * ✅ Mejor organización y legibilidad
 * ✅ Sin dependencias externas
 */
import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  serverTimestamp, 
  onSnapshot, 
  writeBatch,
  deleteField
} from "firebase/firestore";
import { db } from '../config/firebase';

// ============================================
// HELPERS INTERNOS
// ============================================

/**
 * Generar código de invitación único
 */
const generateInviteCode = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
};

/**
 * Función genérica para sincronizar colecciones a Firestore
 * ✅ NUEVO: Elimina duplicación de código
 */
const syncCollectionToFirestore = async (householdId, collectionName, items, options = {}) => {
  try {
    const { useDate = false } = options; // Para mealPlan que usa 'date' como ID
    
    console.log(`🔥 SINCRONIZANDO ${collectionName}: ${items.length} items`);
    
    const collectionRef = collection(db, `households/${householdId}/${collectionName}`);
    const batch = writeBatch(db);
    
    // 1. Obtener documentos existentes
    const snapshot = await getDocs(collectionRef);
    const remoteIds = new Set(snapshot.docs.map(doc => doc.id));
    
    // 2. Identificar IDs locales
    const localIds = new Set(items.map(item => useDate ? item.date : item.id));
    
    let operations = 0;
    
    // 3. Agregar/actualizar documentos
    items.forEach(item => {
      const docId = useDate ? item.date : item.id;
      
      if (!docId) {
        console.error(`❌ Item sin ${useDate ? 'date' : 'id'}, se ignora:`, item);
        return;
      }
      
      const docRef = doc(collectionRef, docId);
      const docData = useDate 
        ? {
            date: item.date,
            breakfast: item.breakfast || null,
            lunch: item.lunch || null,
            snack: item.snack || null,
            dinner: item.dinner || null,
            cooked: item.cooked || {},
            cancelled: item.cancelled || {},
            lastUpdated: serverTimestamp()
          }
        : { 
            ...item, 
            updatedAt: serverTimestamp() 
          };
      
      batch.set(docRef, docData, { merge: useDate ? false : true });
      operations++;
    });
    
    // 4. Eliminar documentos huérfanos
    remoteIds.forEach(remoteId => {
      if (!localIds.has(remoteId)) {
        console.log(`🗑️ Eliminando ${collectionName} huérfano: ${remoteId}`);
        batch.delete(doc(collectionRef, remoteId));
        operations++;
      }
    });
    
    console.log(`🚀 Ejecutando batch de ${operations} operaciones...`);
    await batch.commit();
    console.log(`✅ ${collectionName.toUpperCase()} SINCRONIZADO`);
    
    return { success: true };
  } catch (error) {
    console.error(`❌ Error sincronizando ${collectionName}:`, error);
    if (error.code === 'permission-denied') {
      console.error('⛔ PERMISO DENEGADO. Revisa las reglas de seguridad.');
    }
    return { success: false, error: error.message };
  }
};

/**
 * Función genérica para obtener colecciones desde Firestore
 * ✅ NUEVO: Elimina duplicación
 */
const getCollectionFromFirestore = async (householdId, collectionName, options = {}) => {
  try {
    const { useDate = false } = options;
    
    const collectionRef = collection(db, `households/${householdId}/${collectionName}`);
    const snapshot = await getDocs(collectionRef);
    
    const items = [];
    snapshot.forEach((doc) => {
      items.push(
        useDate 
          ? { date: doc.id, ...doc.data() }
          : { id: doc.id, ...doc.data() }
      );
    });
    
    return { success: true, [collectionName]: items };
  } catch (error) {
    console.error(`Error obteniendo ${collectionName}:`, error);
    return { success: false, error: error.message };
  }
};

/**
 * Función genérica para suscribirse a colecciones en tiempo real
 * ✅ NUEVO: Elimina duplicación
 */
const subscribeToCollection = (householdId, collectionName, callback, options = {}) => {
  const { useDate = false } = options;
  
  const collectionRef = collection(db, `households/${householdId}/${collectionName}`);
  
  return onSnapshot(collectionRef, (snapshot) => {
    const items = [];
    snapshot.forEach((doc) => {
      items.push(
        useDate
          ? { date: doc.id, ...doc.data() }
          : { id: doc.id, ...doc.data() }
      );
    });
    callback(items);
  });
};

// ============================================
// HOUSEHOLDS - Gestión de hogares
// ============================================

/**
 * Crear nuevo hogar
 */
export const createHousehold = async (userId, householdName) => {
  try {
    const householdId = `household_${Date.now()}`;
    const inviteCode = generateInviteCode();
    
    const householdRef = doc(db, 'households', householdId);
    const inviteRef = doc(db, 'householdInvites', inviteCode);
    const userRef = doc(db, 'users', userId);
    const batch = writeBatch(db);
    
    batch.set(householdRef, {
      name: householdName,
      inviteCode,
      members: {
        [userId]: 'admin' 
      },
      ownerId: userId,
      createdBy: userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    batch.set(inviteRef, {
      householdId,
      active: true,
      createdBy: userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    batch.set(userRef, { 
      householdId: householdId 
    }, { merge: true });

    await batch.commit();
    
    return { 
      success: true, 
      householdId, 
      inviteCode 
    };
  } catch (error) {
    console.error('Error creando hogar:', error);
    return { 
      success: false, 
      error: error.message 
    };
  }
};

/**
 * Unirse a hogar con código
 */
export const joinHousehold = async (userId, inviteCode) => {
  try {
    // 1. Buscar si existe una casa con ese código
    const code = String(inviteCode || '').trim().toUpperCase();
    if (!code) {
      return { success: false, error: 'Ingresa un cÃ³digo de invitaciÃ³n.' };
    }

    const inviteRef = doc(db, 'householdInvites', code);
    const inviteSnap = await getDoc(inviteRef);

    if (!inviteSnap.exists() || inviteSnap.data().active !== true) {
      return { success: false, error: 'Código de invitación inválido o no existe.' };
    }

    const householdId = inviteSnap.data().householdId;

    // 2. Verificar si el usuario ya está adentro
    if (!householdId) {
       return { success: false, error: 'El cÃ³digo de invitaciÃ³n estÃ¡ incompleto.' };
    }

    // 3. Crear el "Batch" (hacer los dos cambios al mismo tiempo o ninguno)
    const batch = writeBatch(db);

    // Cambio A: Agregar al usuario al mapa de miembros de la casa
    const householdRef = doc(db, 'households', householdId);
    batch.update(householdRef, {
      [`members.${userId}`]: 'member', // Se agrega con rol de miembro raso
      joinProof: {
        uid: userId,
        inviteCode: code,
        at: new Date()
      },
      updatedAt: serverTimestamp()
    });

    // Cambio B: Ponerle la medallita (householdId) al perfil del usuario
    const userRef = doc(db, 'users', userId);
    batch.set(userRef, {
      householdId: householdId
    }, { merge: true });

    // Ejecutar todo junto
    await batch.commit();

    return { success: true, householdId };

  } catch (error) {
    console.error("❌ Error uniéndose al hogar:", error);
    return { success: false, error: 'Error de conexión. Intenta de nuevo.' };
  }
};

/**
 * Abandonar hogar
 */
export const leaveHousehold = async (userId, householdId) => {
  try {
    console.log('🚪 Abandonando hogar...');
    console.log('👤 userId:', userId);
    console.log('🏠 householdId:', householdId);
    
    const householdRef = doc(db, 'households', householdId);
    const householdDoc = await getDoc(householdRef);
    
    if (!householdDoc.exists()) {
      return { 
        success: false, 
        error: 'Hogar no encontrado' 
      };
    }
    
    const householdData = householdDoc.data();
    const memberCount = Object.keys(householdData.members || {}).length;
    const userRef = doc(db, 'users', userId);
    const batch = writeBatch(db);
    
    if (memberCount <= 1) {
      console.log('🗑️ Eres el único miembro, eliminando hogar...');
      batch.delete(householdRef);
    } else {
      console.log('➖ Eliminando usuario de la lista de miembros...');
      batch.update(householdRef, {
        [`members.${userId}`]: deleteField(),
        updatedAt: serverTimestamp()
      });
    }
    
    console.log('👤 Actualizando perfil del usuario...');
    batch.set(userRef, {
      householdId: null
    }, { merge: true });

    await batch.commit();
    
    console.log('✅ Hogar abandonado exitosamente');
    return { 
      success: true 
    };
  } catch (error) {
    console.error('❌ Error abandonando hogar:', error);
    return { 
      success: false, 
      error: error.message 
    };
  }
};

/**
 * Obtener datos del hogar
 */
export const getHousehold = async (householdId) => {
  try {
    const householdRef = doc(db, 'households', householdId);
    const householdDoc = await getDoc(householdRef);
    
    if (!householdDoc.exists()) {
      return { 
        success: false, 
        error: 'Hogar no encontrado' 
      };
    }
    
    return { 
      success: true, 
      household: { 
        id: householdDoc.id, 
        ...householdDoc.data() 
      } 
    };
  } catch (error) {
    console.error('Error obteniendo hogar:', error);
    return { 
      success: false, 
      error: error.message 
    };
  }
};

/**
 * Listener en tiempo real del hogar
 */
export const subscribeToHousehold = (householdId, callback) => {
  const householdRef = doc(db, 'households', householdId);
  return onSnapshot(householdRef, (doc) => {
    if (doc.exists()) {
      callback({ 
        id: doc.id, 
        ...doc.data() 
      });
    }
  });
};

// ============================================
// STOCK - Gestión de inventario
// ============================================

export const syncStockToFirestore = async (householdId, stockItems) => {
  return syncCollectionToFirestore(householdId, 'stock', stockItems);
};

export const getStockFromFirestore = async (householdId) => {
  const result = await getCollectionFromFirestore(householdId, 'stock');
  return result.success 
    ? { success: true, stock: result.stock } 
    : result;
};

export const subscribeToStock = (householdId, callback) => {
  return subscribeToCollection(householdId, 'stock', callback);
};

// ============================================
// RECIPES - Gestión de recetas
// ============================================

export const syncRecipesToFirestore = async (householdId, recipes) => {
  return syncCollectionToFirestore(householdId, 'recipes', recipes);
};

export const getRecipesFromFirestore = async (householdId) => {
  const result = await getCollectionFromFirestore(householdId, 'recipes');
  return result.success 
    ? { success: true, recipes: result.recipes } 
    : result;
};

export const subscribeToRecipes = (householdId, callback) => {
  return subscribeToCollection(householdId, 'recipes', callback);
};

// ============================================
// MEAL PLAN - Gestión de planificación
// ============================================

export const syncMealPlanToFirestore = async (householdId, mealPlanItems) => {
  return syncCollectionToFirestore(householdId, 'mealPlan', mealPlanItems, { useDate: true });
};

export const getMealPlanFromFirestore = async (householdId) => {
  const result = await getCollectionFromFirestore(householdId, 'mealPlan', { useDate: true });
  return result.success 
    ? { success: true, mealPlan: result.mealPlan } 
    : result;
};

export const subscribeToMealPlan = (householdId, callback) => {
  return subscribeToCollection(householdId, 'mealPlan', callback, { useDate: true });
};

// ============================================
// SHOPPING LISTS - Gestión de listas de compras
// ============================================

export const syncShoppingListsToFirestore = async (householdId, shoppingLists) => {
  return syncCollectionToFirestore(householdId, 'shoppingLists', shoppingLists);
};

export const getShoppingListsFromFirestore = async (householdId) => {
  const result = await getCollectionFromFirestore(householdId, 'shoppingLists');
  return result.success 
    ? { success: true, shoppingLists: result.shoppingLists } 
    : result;
};

export const subscribeToShoppingLists = (householdId, callback) => {
  return subscribeToCollection(householdId, 'shoppingLists', callback);
};

// ============================================
// USER - Gestión de usuarios
// ============================================

/**
 * Crear perfil de usuario
 */
export const createUserProfile = async (userData) => {
  try {
    const userRef = doc(db, 'users', userData.uid);
    
    const userProfile = {
      email: userData.email,
      displayName: userData.displayName || 'Usuario',
      photoURL: userData.photoURL || null,
      householdId: null,
      createdAt: serverTimestamp()
    };
    
    await setDoc(userRef, userProfile);
    
    return { 
      success: true, 
      user: { 
        id: userData.uid, 
        ...userProfile 
      } 
    };
  } catch (error) {
    console.error('Error creando perfil:', error);
    return { 
      success: false, 
      error: error.message 
    };
  }
};

/**
 * Obtener perfil de usuario
 */
export const getUserProfile = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      return { 
        success: false, 
        error: 'Usuario no encontrado' 
      };
    }
    
    return { 
      success: true, 
      user: { 
        id: userDoc.id, 
        ...userDoc.data() 
      } 
    };
  } catch (error) {
    console.error('Error obteniendo perfil:', error);
    return { 
      success: false, 
      error: error.message 
    };
  }
};
